import React from 'react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { Button, Card, CardContent, CardMedia, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { NavigatorEntry } from './NavigatorData';
import { AuthContext } from '../authentication/AuthContext';
import { DownloadProductTypes, DownloadInfo, ProductsDataSet, getAllProductData, findProductData, findServerData, initialiseDownload} from './DownloadProductData';
import { DeltaXMLColors, MyDeltaColors } from './styling/DeltaColors';
import { DTFormatter } from '../general/datetime-formatter/DTFormatter';

const styles = (theme: Theme) =>
    createStyles({
        paper: {
            maxWidth: 880,
            margin: 'auto',
            overflow: 'hidden',
        },
        contentWrapper: {
            width: 800,
            margin: '40px 40px',
            verticalAlign: 'middle',
        },
        root: {
            width: 800,
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            verticalAlign: 'middle',
        },
        button: {
            float: 'right',
            margin: '10px',
            width: 200,
            height: 40,
        },
        previousReleasesButton: {
            marginRight: '10px',
            width: 75,
            height: 25,
        },
        cover: {
            height: 380,
            width: 300,
            backgroundColor: DeltaXMLColors.OXFORD_BLUE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            verticalAlign: 'middle',
        },
        cardDetailsDiv: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            verticalAlign: 'middle',
        },
        image: {
            height: 85,
            width: 100,
            verticalAlign: 'middle'
        },
        cardContent: {
            width: 800
        },
        dealsRowCellText: {
            backgroundColor: DeltaXMLColors.CELERY,
            color: MyDeltaColors.WHITE,
            padding: '8px',
            fontWeight: 600
        },
        tableContainer: {
            borderRadius: 0,
            maxHeight: 'calc(100vh - 600px)'
        },
        textColor: {
            color: DeltaXMLColors.OXFORD_BLUE,
        },
        cardTitles: {
            color: DeltaXMLColors.CELERY,
        }
    });

export interface ProductInfoPageProps extends WithStyles<typeof styles> {
    // added in order to use same component but to show differences on change in URL
    // could be removed once speicific components for various products have been created
    productName?: string;
    downloadServerSections?: NavigatorEntry[];
    downloadSections: NavigatorEntry[];
    downloadProductsData: ProductsDataSet;
    dateFormat: string;
}

function ProductInfoPage(props: ProductInfoPageProps) {
    const { classes, productName, downloadServerSections, downloadSections, downloadProductsData, dateFormat } = props;
    const auth = React.useContext(AuthContext);

    let productDownload: DownloadInfo = initialiseDownload;
    let productDownloadDotNet: DownloadInfo = initialiseDownload;
    let productVersions: DownloadInfo[] = [];

    let serverDownload: DownloadInfo = initialiseDownload;
    let serverPlatform: string = "";
    let serverNameOS: string = "";
    let serverVersions: DownloadInfo[] = [];

    const setProductVersions = (productId: string) => {
        productVersions = getAllProductData(downloadProductsData, productId, undefined);
    }
    const setServerVersions = () => {
        serverVersions = getAllProductData(downloadProductsData, undefined, serverNameOS);
    }

    const findProduct = (productId: string) => {
        [productDownload, productDownloadDotNet] = findProductData(productId, downloadProductsData);
    }

    const findServer = (serverid: string) => {
        [serverDownload, serverPlatform, serverNameOS] = findServerData(serverid, downloadProductsData);
    }

    function displayPreviousVersions(rows: DownloadInfo[]) {
        let tableRows: any = [];
        rows.map((row) => {
            if (row.current_release !== true) {
                if (row.filename.includes("_j.zip") || row.filename.includes('licensing') || row.filename.includes('DeltaJSON') || row.filename.includes('Data-Compare')) {
                    // Find all of the .NET version of the product, and select the currently processed one based on a version number
                    let dotNetProducts = rows.filter(item => item.filename.includes("_n.zip"))
                    let currentDotNetProduct = dotNetProducts.find(item => item.version === row.version)

                    tableRows.push(
                        <TableRow key={row.filename}>
                            <TableCell >{row.version}</TableCell>
                            <TableCell >{DTFormatter.getFormattedDate(row.release_date, dateFormat)}</TableCell>
                            <TableCell >
                                <Button className={classes.previousReleasesButton} variant="contained" size="small" color="primary"
                                    onClick={() => { window.location.href = row.download_url }}>
                                    Java
                                </Button>
                                
                                {currentDotNetProduct !== undefined &&
                                    <Button className={classes.previousReleasesButton} variant="contained" size="small" color="primary"
                                        onClick={() => { window.location.href = currentDotNetProduct?.download_url ?? "" }}> {/* <-- This unwrap here is just to silent TypeScript error. It won't be ever excuted as we check against 'undefined' 2 lines above */}
                                        .NET
                                    </Button>
                                }
                            </TableCell>
                        </TableRow>
                    )
                }
                else {
                    // Check if Java equivalent of the currently processed .NET product exists
                    let javaVersionExists = rows.find(item => item.filename.includes("_j.zip") && item.version === row.version) ? true : false
                    let javaOrDotNet = row.filename.includes("_n.zip") ? ".NET" : "Java"

                    if (!javaVersionExists || javaOrDotNet==="Java") {
                        tableRows.push(
                            <TableRow key={row.filename}>
                                <TableCell >{row.version}</TableCell>
                                <TableCell >{DTFormatter.getFormattedDate(row.release_date, dateFormat)}</TableCell>
                                <TableCell >
                                    <Button className={classes.previousReleasesButton} variant="contained" size="small" color="primary"
                                        onClick={() => { window.location.href = row.download_url }}>
                                        {javaOrDotNet}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        )
                    }
                }
            }
        })
        return tableRows;
    }


    return (
        <Paper className={classes.paper}>
            {downloadSections.map(({ id: productId, link: productRefLink, icon: productIcon, productInfo: productInfo }) => {
                if (productRefLink.includes(productName!)) {
                    return (
                        <>
                            {findProduct(productId)}
                            <AppBar position="static" elevation={0} style={{ backgroundColor: auth.isAuthenticated() ? DeltaXMLColors.DARK_BACKGROUND : DeltaXMLColors.OXFORD_BLUE }}>
                                <Toolbar>
                                    <Typography variant="h4">{productId}</Typography>
                                </Toolbar>
                            </AppBar>
                            <div className={classes.contentWrapper}>
                                <Card className={classes.root}>
                                    <div className={classes.cover}>
                                        <CardMedia component="img"
                                            image={productIcon.props.src}
                                            title={productId} className={classes.image} />
                                    </div>
                                    <CardContent className={classes.cardContent}>
                                        {productDownload.download_url !== '' &&
                                            <div className={classes.cardDetailsDiv}>
                                                <div style={{ width: '100%' }}>
                                                    <Typography variant="h5" className={classes.cardTitles}><u><b>Java</b></u></Typography>
                                                    <Typography variant="body2" className={classes.textColor} ><b>Current Version :</b>&nbsp;{productDownload.version}</Typography>
                                                    <Typography variant="body2" className={classes.textColor}><b>Release Date :</b>&nbsp;{DTFormatter.getFormattedDate(productDownload.release_date, dateFormat)}</Typography>
                                                    <Typography variant="body2" className={classes.textColor}><b>Documentation :</b>&nbsp;<a style={{ color: DeltaXMLColors.FOUNTAIN_BLUE }} href={productInfo} target="_blank" rel="noreferrer">Click here</a></Typography>
                                                    <Typography variant="body2" className={classes.textColor}><b>Platforms :</b>&nbsp;Java (All OS)</Typography>
                                                </div>
                                                <div style={{ width: '100%' }}>
                                                    {productDownload.download_url !== '' &&
                                                        <Button className={classes.button} variant="contained" color="primary"
                                                            onClick={() => { window.location.href = productDownload.download_url }}>
                                                            Download Java Version
                                                        </Button>
                                                    }
                                                </div>
                                            </div>
                                        }
                                        <br />
                                        {productDownloadDotNet.download_url !== '' &&
                                            <div className={classes.cardDetailsDiv}>
                                                <div style={{ width: '100%' }}>
                                                    <Typography variant="h5" className={classes.cardTitles}><u><b>.NET</b></u></Typography>
                                                    <Typography variant="body2" className={classes.textColor}><b>Current Version :</b>&nbsp;{productDownloadDotNet.version}</Typography>
                                                    <Typography variant="body2" className={classes.textColor}><b>Release Date :</b>&nbsp;{DTFormatter.getFormattedDate(productDownloadDotNet.release_date, dateFormat)}</Typography>
                                                    <Typography variant="body2" className={classes.textColor}><b>Documentation :</b>&nbsp;<a style={{ color: DeltaXMLColors.FOUNTAIN_BLUE }} href={productInfo} target="_blank" rel="noreferrer">Click here</a></Typography>
                                                    <Typography variant="body2" className={classes.textColor}><b>Platforms :</b>&nbsp;.NET</Typography>
                                                </div>
                                                <div style={{ width: '100%' }}>
                                                    {productDownloadDotNet.download_url !== '' &&
                                                        <Button className={classes.button} variant="contained" color="primary"
                                                            onClick={() => { window.location.href = productDownloadDotNet.download_url }}>
                                                            Download .NET Version
                                                        </Button>
                                                    }
                                                </div>
                                            </div>
                                        }
                                    </CardContent>
                                </Card>
                            </div>
                            {setProductVersions(productId)}
                            <div className={classes.contentWrapper}>
                                {(productVersions.length !== 0 && !productVersions[0].filename.includes("licensing")) && <TableContainer className={classes.tableContainer} component={Paper}>
                                    <Table aria-label="collapsible table">
                                        <colgroup>
                                            <col width="33%" />
                                            <col width="33%" />
                                            <col width="33%" />
                                        </colgroup>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className={classes.dealsRowCellText}>Version</TableCell>
                                                <TableCell className={classes.dealsRowCellText}>Release Date</TableCell>
                                                <TableCell className={classes.dealsRowCellText}>Download</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(productVersions.length !== 0)
                                                ? <React.Fragment >
                                                    {displayPreviousVersions(productVersions)}</React.Fragment>
                                                : <></>}
                                        </TableBody>
                                    </Table>
                                </TableContainer>}
                            </div>
                        </>
                    )
                }
            })}
            {downloadServerSections !== undefined &&
             downloadServerSections.map(({ id: productId, link: productRefLink, icon: productIcon, productInfo: productInfo }) => {
                if (productRefLink.includes(productName!)) {
                    return (
                        <>
                            {findServer(productId)}
                            <AppBar position="static" elevation={0} style={{ backgroundColor: auth.isAuthenticated() ? DeltaXMLColors.DARK_BACKGROUND : DeltaXMLColors.OXFORD_BLUE}}>
                                <Toolbar>
                                    <Typography variant="h4">{productId}</Typography>
                                </Toolbar>
                            </AppBar>
                            <div className={classes.contentWrapper}>
                                <Card className={classes.root}>
                                    <div className={classes.cover}>
                                        <CardMedia component="img"
                                            image={productIcon.props.src}
                                            title={productId} className={classes.image} />
                                    </div>
                                    <CardContent className={classes.cardContent}>
                                        {serverDownload.download_url !== '' &&
                                            <div className={classes.cardDetailsDiv}>
                                                <div style={{ width: '100%' }}>
                                                    <Typography variant="h5" className={classes.cardTitles}><u><b>{
                                                     serverPlatform.includes(DownloadProductTypes.linuxServer) ? DownloadProductTypes.linuxServer : 
                                                     serverPlatform.includes(DownloadProductTypes.windowsServer) ? DownloadProductTypes.windowsServer : 
                                                     serverPlatform.includes(DownloadProductTypes.macIntelServer) ? DownloadProductTypes.macIntelServer : 
                                                     DownloadProductTypes.macARMServer
                                                    }</b></u></Typography>
                                                    <Typography variant="body2" className={classes.textColor}><b>Current Version :</b>&nbsp;{serverDownload.version}</Typography>
                                                    <Typography variant="body2" className={classes.textColor}><b>Release Date :</b>&nbsp;{DTFormatter.getFormattedDate(serverDownload.release_date, dateFormat)}</Typography>
                                                    <Typography variant="body2" className={classes.textColor}><b>Documentation :</b>&nbsp;<a style={{ color: DeltaXMLColors.FOUNTAIN_BLUE }} href={productInfo} target="_blank" rel="noreferrer">Click here</a></Typography>
                                                    <Typography variant="body2" className={classes.textColor}><b>Platforms :</b>&nbsp;{serverPlatform}</Typography>
                                                </div>
                                                <div style={{ width: '100%' }}>
                                                    {serverDownload.download_url !== '' &&
                                                        <Button className={classes.button} variant="contained" color="primary"
                                                            onClick={() => { window.location.href = serverDownload.download_url }}>
                                                            Download
                                                        </Button>
                                                    }
                                                </div>
                                            </div>
                                        }
                                    </CardContent>
                                </Card>
                            </div>
                            {setServerVersions()}
                            <div className={classes.contentWrapper}>
                                {(serverVersions.length > 1) && <TableContainer className={classes.tableContainer} component={Paper}>
                                    <Table aria-label="collapsible table">
                                        <colgroup>
                                            <col width="33%" />
                                            <col width="33%" />
                                            <col width="33%" />
                                        </colgroup>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className={classes.dealsRowCellText}>Version</TableCell>
                                                <TableCell className={classes.dealsRowCellText}>Release Date</TableCell>
                                                <TableCell className={classes.dealsRowCellText}>Download</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(serverVersions.length !== 0)
                                                ? <React.Fragment >
                                                    {displayPreviousVersions(serverVersions)}</React.Fragment>
                                                : <></>}
                                        </TableBody>
                                    </Table>
                                </TableContainer>}
                            </div>
                        </>
                    )
                }
            })}
        </Paper>
    );
}

export default withStyles(styles)(ProductInfoPage);